import { Button, Dialog } from '@ui'
import { motion } from 'framer-motion'
import React, { CSSProperties, FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  scaleFactor?: number
  children?: ReactNode
  onClick?: () => void
  disableHover?: boolean
  style?: CSSProperties
  tabIndex?: number
  disabled?: boolean
  layoutId?: string
}

const Touchable: FC<Props> = ({
  children,
  scaleFactor = 0.01,
  onClick,
  disableHover,
  style,
  tabIndex,
  disabled = false,
  layoutId,
}) => (
  <motion.div
    transition={{
      type: 'spring',
      bounce: 0.5,
    }}
    whileHover={disableHover ? undefined : { scale: 1 + scaleFactor }}
    whileTap={{ scale: 1 - scaleFactor }}
    style={{ cursor: disabled ? 'default' : 'pointer', ...style }}
    tabIndex={tabIndex}
    onClick={(e) => {
      if (onClick) {
        e.stopPropagation()
        onClick()
      }
    }}
    aria-disabled={disabled}
    layoutId={layoutId}
  >
    {children}
  </motion.div>
)

export const TouchableWithConfirmation = ({
  confirmText,
  confirmButtonText,
  onClick,
  ...props
}: Props & { confirmText?: string; confirmButtonText?: string }) => {
  const [promptVisible, setPromptVisible] = useState(false)

  const { t } = useTranslation()

  return (
    <>
      <Touchable {...props} onClick={() => setPromptVisible(true)} />

      <Dialog
        open={promptVisible}
        onClose={() => setPromptVisible(false)}
        title={confirmText || t('common.areYouSure')}
        actions={
          <>
            <Button
              variant="danger"
              fullWidth
              onClick={() => {
                setPromptVisible(false)
                onClick?.()
              }}
            >
              {confirmButtonText || t('common.yes')}
            </Button>
            <Button variant="secondary" fullWidth onClick={() => setPromptVisible(false)}>
              {t('common.cancel')}
            </Button>
          </>
        }
      />
    </>
  )
}

export default Touchable
